import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * StatCard Component
 *
 * A standardized stat card component with fixed dimensions.
 * All stat cards use the same size regardless of content length.
 * Ensures consistent alignment and sizing across stat displays.
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Stat label text
 * @param {string|number} props.value - Stat value to display
 * @param {string} props.valueVariant - Typography variant for value (default: 'h4')
 * @param {string} props.labelVariant - Typography variant for label (default: 'subtitle2')
 * @param {Object} props.sx - Additional sx styles to merge
 * @param {number} props.minHeight - Minimum height for the card (default: 120)
 */
const StatCard = ({
  label,
  value,
  valueVariant = 'h4',
  labelVariant = 'subtitle2',
  sx = {},
  minHeight = 120,
}) => {
  return (
    <Card
      sx={{
        height: '100%',
        minHeight,
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant={labelVariant}
          color="text.secondary"
          gutterBottom
          sx={{
            minHeight: '24px',
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            minHeight: '48px',
            flexGrow: 1,
          }}
        >
          <Typography
            variant={valueVariant}
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  valueVariant: PropTypes.string,
  labelVariant: PropTypes.string,
  sx: PropTypes.object,
  minHeight: PropTypes.number,
};

export default StatCard;
